import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import _ from 'lodash';
import trackEvents from '@/helpers/trackEvents';
import * as QuestionnaireApiModels from '@/typings/questionnaires-api-models';
import QuestionnaireFormContext, { QuestionnaireFormContextType } from './QuestionnaireFormContext';
import useQuestionnaireContext from '../useQuestionnaireContext';

type QuestionnaireFormProviderProps = {
  formMethods: QuestionnaireFormContextType['formMethods'];
  id: string;
};

const isRowFieldKey = (rowFieldKey: string, fullFieldKey: string): boolean => (
  fullFieldKey === rowFieldKey
  || fullFieldKey.startsWith(`${rowFieldKey}[`)
  || fullFieldKey.startsWith(`${rowFieldKey}.`)
);

const QuestionnaireFormProvider: React.FC<QuestionnaireFormProviderProps> = ({
  children,
  formMethods,
  id,
}) => {
  trackEvents.track('Render QuestionnaireFormProvider', id);
  const { questionnaireResponses } = useQuestionnaireContext();
  const omitForPdfFillsKey = 'mastercreditorlistfield59';
  const keyForLineNumber = 'mastercreditorlistfield49';
  const responseSourcesRef = useRef<Record<string, QuestionnaireApiModels.QuestionnaireResponse['source']>>({});
  const getResponseSource = useCallback<QuestionnaireFormContextType['getResponseSource']>((fieldKey) => responseSourcesRef.current[fieldKey], []);
  const setResponseSource = useCallback<QuestionnaireFormContextType['setResponseSource']>((fieldKey, source) => {
    responseSourcesRef.current = {
      ...responseSourcesRef.current,
      [fieldKey]: source,
    };
  }, []);
  const getRowResponseSources = useCallback<QuestionnaireFormContextType['getRowResponseSources']>((rowFieldKey) => {
    const subFieldSources: ReturnType<QuestionnaireFormContextType['getRowResponseSources']> = {};
    Object.entries(responseSourcesRef.current).forEach(([fullFieldKey, source]) => {
      if (isRowFieldKey(rowFieldKey, fullFieldKey)) {
        subFieldSources[fullFieldKey] = source;
      }
    });
    return subFieldSources;
  }, []);
  const setRowResponseSources = useCallback<QuestionnaireFormContextType['setRowResponseSources']>((rowFieldKey, sources) => {
    responseSourcesRef.current = {
      ...responseSourcesRef.current,
      ...sources,
    };
  }, []);

  const aiRequestIdRef = useRef<Record<string, string | null>>({});
  const getAiRequestId = useCallback<QuestionnaireFormContextType['getAiRequestId']>((fieldKey): string | null => aiRequestIdRef.current[fieldKey] ?? null, []);
  const setAiRequestId = useCallback<QuestionnaireFormContextType['setAiRequestId']>((fieldKey, aiRequestIdLocal) => {
    aiRequestIdRef.current = {
      ...aiRequestIdRef.current,
      [fieldKey]: aiRequestIdLocal,
    };
  }, []);
  const getRowAiRequestIds = useCallback<QuestionnaireFormContextType['getRowAiRequestIds']>((rowFieldKey) => {
    const subFieldAiRequestIds: ReturnType<QuestionnaireFormContextType['getRowAiRequestIds']> = {};
    Object.entries(aiRequestIdRef.current).forEach(([fullFieldKey, aiRequestId]) => {
      if (isRowFieldKey(rowFieldKey, fullFieldKey)) {
        subFieldAiRequestIds[fullFieldKey] = aiRequestId;
      }
    });
    return subFieldAiRequestIds;
  }, []);
  const setRowAiRequestIds = useCallback<QuestionnaireFormContextType['setRowAiRequestIds']>((rowFieldKey, aiRequestIds) => {
    aiRequestIdRef.current = {
      ...aiRequestIdRef.current,
      ...aiRequestIds,
    };
  }, []);

  useEffect(() => {
    const newFieldSources: Record<string, QuestionnaireApiModels.QuestionnaireResponse['source']> = {};
    const newAiRequestIds: Record<string, string | null> = {};
    (questionnaireResponses ?? []).forEach(fieldResponse => {
      if (fieldResponse.fieldTemplate.type !== 'list' && fieldResponse.fieldTemplate.type !== 'table') {
        newFieldSources[fieldResponse.fieldTemplate.key] = fieldResponse.source;
        newAiRequestIds[fieldResponse.fieldTemplate.key] = fieldResponse.aiRequestId ?? null;
      } else {
        const listSources = fieldResponse.listResponses?.reduce(
          (acc: Record<string, QuestionnaireApiModels.QuestionnaireResponse['source']>, item) => ({
            ...acc,
            [`${fieldResponse.fieldTemplate.key}[${item.index}].${item.fieldTemplate.key}`]: item.source,
          }),
          {},
        );
        const listAiRquests = fieldResponse.listResponses?.reduce(
          (acc: Record<string, string | null>, item) => {
            if(item.source!=='ai') {
              return acc;
            }
            return (
              {
                ...acc,
                [`${fieldResponse.fieldTemplate.key}[${item.index}].${item.fieldTemplate.key}`]: item.aiRequestId ?? null,
              }
            );
          },
          {},
        );
        Object.assign(newFieldSources, listSources);
        Object.assign(newAiRequestIds, listAiRquests);
      }
    });
    responseSourcesRef.current = newFieldSources;
    aiRequestIdRef.current = newAiRequestIds;
  }, [questionnaireResponses]);

  const setValue = useCallback<QuestionnaireFormContextType['setValue']>(
    ({ key, value, source, aiRequestId }): void => {
      // @todo - it would be nice if we checked the source here and didn't allow setting if "manual" unless new source is also "manual"
      const splitKeyForAccountStatus = key.replace(/\[(\w+)\]/g, '.$1')?.split('.');
      const [masterKey,index,fieldKey] = splitKeyForAccountStatus;
      let targettedValue= '';
      if(masterKey && Number(index) >= 0) {
        const valueForMasterKey = formMethods.getValues()[masterKey] as [];
        if(valueForMasterKey?.length) {
          targettedValue = valueForMasterKey[Number(index)]?.[omitForPdfFillsKey];
        }
      }
      if(keyForLineNumber !== fieldKey || !Array.isArray(targettedValue) || !targettedValue?.includes('omit') ) {
        formMethods.setValue(key, value, { shouldDirty: true });
      }
      if (source) {
        setResponseSource(key, source);
      }
      if(aiRequestId) {
        setAiRequestId( key, aiRequestId);
      }
    },
    [formMethods, setResponseSource, setAiRequestId],
  );

  const values = useMemo(() => {
    trackEvents.track('QuestionnaireFormContext - valuesMemoized');
    const newContextValues: QuestionnaireFormContextType = {
      formMethods,
      getAiRequestId,
      getResponseSource,
      getRowAiRequestIds,
      getRowResponseSources,
      setAiRequestId,
      setResponseSource,
      setRowAiRequestIds,
      setRowResponseSources,
      setValue,
    };
    return newContextValues;
  }, [
    formMethods,
    setValue,
    setAiRequestId,
    getAiRequestId,
    getRowAiRequestIds,
    getRowResponseSources,
    getResponseSource,
    setResponseSource,
    setRowAiRequestIds,
    setRowResponseSources,
  ]);

  return (
    <QuestionnaireFormContext.Provider value={values}>
      {children}
    </QuestionnaireFormContext.Provider>
  );
};

export default QuestionnaireFormProvider;
